import type { ReactNode } from "react";
import type { IconType } from "react-icons";

type PageHeaderProps = {
  title: ReactNode;
  description?: ReactNode;
  fullWidthDescription?: boolean;
  icon?: IconType;
  action?: ReactNode;
  /** Tabbed pages supply their own divider immediately below the header. */
  divider?: boolean;
  compactMobile?: boolean;
};

export default function PageHeader({
  title,
  description,
  fullWidthDescription = false,
  icon: Icon,
  action,
  divider = true,
  compactMobile = false,
}: PageHeaderProps) {
  const headerSpacing = divider
    ? compactMobile
      ? "mb-3 border-b border-athens-gray-200 pb-3 md:mb-8 md:pb-6"
      : "mb-8 border-b border-athens-gray-200 pb-6"
    : compactMobile
      ? "mb-2 md:mb-4"
      : "mb-4";

  return (
    <header className={headerSpacing}>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-3">
            {Icon && (
              <Icon
                aria-hidden="true"
                className="h-6 w-6 shrink-0 text-orange-400"
              />
            )}
            <h1
              className={`min-w-0 font-semibold leading-tight tracking-tight text-slate-900 md:text-[32px] ${
                compactMobile
                  ? "truncate whitespace-nowrap text-xl"
                  : "text-3xl"
              }`}
            >
              {title}
            </h1>
          </div>
          {description && (
            <p
              className={`mt-2 ${compactMobile ? "hidden md:block" : ""} ${
                fullWidthDescription ? "" : "max-w-2xl"
              } text-base leading-6 text-slate-600`}
            >
              {description}
            </p>
          )}
        </div>
        {action && <div className="shrink-0">{action}</div>}
      </div>
    </header>
  );
}
